import React from 'react';
import { useQueries, useQueryClient } from 'react-query';
import API, { Types, } from '@wheeebo/polaris-api';
import PolarisMap from './Map';
import useMap from './useMap';
import AsvMarker from './AsvMarker';
import DroneMarker from './DroneMarker';
import HoverbikeMarker from './HoverbikeMarker';
import useMonitorMap from './useMonitorMap';
import { useErrorHandler } from '../../errorHandler';
import { isLikelyUserMapCenter, pickRadarCenter } from './pickRadarCenter';
const INITIAL_ITEM_COUNT = 100;
const ANIMATION_FRAME_RATES = 12;
const MAP_UPDATE_INTERVAEL_MSEC = 5000;
const NO_FLY_ZONE_SEARCH_RADIUS_METER = 10000;
const MOVEMENT_SPEED_FACTOR = 1 / 3; // 移動速度を1/3に抑えてカクカクを軽減
function MonitorMap({ apiKey, missionId, selectedFlightPathId, children, on3dSelected }) {
    const queryClient = useQueryClient();
    const { loaded, center, setBounds, mapTypeId } = useMap();
    const { vehicles, setVehicles, radarRadius, selectedVehicleId, setSelectedVehicleId, showFootprint, showRadar, showFlightPath, showGeoFence, showSafePort, showNoFlyZone, } = useMonitorMap();
    const { showAndEmitJsErrorEvent } = useErrorHandler();
    const [previousLoadTime, setPreviousLoadTime] = React.useState();
    const [timeoutHandler, setTimeoutHandler] = React.useState();
    const [vehiclesToDisplay, setVehiclesToDisplay] = React.useState();
    const [geoFenceToDisplay, setGeoFenceToDisplay] = React.useState();
    const [safePortToDisplay, setSafePortToDisplay] = React.useState();
    const [flightPathToDisplay, setFlightPathToDisplay] = React.useState();
    const flightPathOptions = { missionId, preload: ['waypoints'] };
    const safePortOptions = {};
    const vehicleOptions = {
        ids: vehicles?.map((e) => e.vehicle.id),
        missionId,
        preload: ['waypoints', 'safe_ports', 'geo_fences'],
    };
    const noFlyZoneOptions = {
        latitude: center?.lat || 0,
        longitude: center?.lng || 0,
        radius: NO_FLY_ZONE_SEARCH_RADIUS_METER,
    };
    const [vehicleLocationMap, setVehicleLocationMap] = React.useState(new Map());
    const options = {
        limit: INITIAL_ITEM_COUNT,
        latitude: center?.lat || 0,
        longitude: center?.lng || 0,
        radius: radarRadius,
        missionId,
    };
    const [{ data: geoFences }, { data: flightPaths }, { data: safePorts }, { data: noFlyZones }, { data: vehicleList }, { data: footprints },] = useQueries([
        {
            queryKey: missionId ? API.GeoFences.listKey(missionId, {}) : '',
            queryFn: () => (missionId ? API.GeoFences.list(missionId, {}) : undefined),
            enabled: !!missionId,
            keepPreviousData: true,
        },
        {
            queryKey: missionId ? API.FlightPaths.listKey(flightPathOptions) : '',
            queryFn: () => (missionId ? API.FlightPaths.list(flightPathOptions) : undefined),
            enabled: !!missionId,
            keepPreviousData: true,
        },
        {
            queryKey: missionId ? API.SafePorts.listKey(missionId, safePortOptions) : '',
            queryFn: () => (missionId ? API.SafePorts.list(missionId, safePortOptions) : undefined),
            enabled: !!missionId,
            keepPreviousData: true,
        },
        {
            queryKey: API.NoFlyZones.listKey(noFlyZoneOptions),
            queryFn: () => API.NoFlyZones.list(noFlyZoneOptions),
            keepPreviousData: true,
            enabled: isLikelyUserMapCenter(center),
        },
        {
            queryKey: API.Vehicles.listKey(vehicleOptions),
            queryFn: () => API.Vehicles.list(vehicleOptions),
            enabled: (vehicles?.length || 0) > 0,
            keepPreviousData: true,
        },
        {
            queryKey: isLikelyUserMapCenter(center) ? API.Monitor.listKey(options) : '',
            queryFn: () => (isLikelyUserMapCenter(center) ? API.Monitor.list(options) : undefined),
            enabled: isLikelyUserMapCenter(center) && radarRadius >= 100 && radarRadius <= 100000,
            refetchInterval: MAP_UPDATE_INTERVAEL_MSEC,
            onError: showAndEmitJsErrorEvent,
            onSuccess: (data) => {
                clearTimeout(timeoutHandler);
                if (!data?.items || data.items.length <= 0) {
                    vehicleLocationMap.clear();
                    setVehicleLocationMap?.(new Map(vehicleLocationMap));
                    setVehicles?.([]);
                    if (!missionId) {
                        setFlightPathToDisplay([]);
                        setGeoFenceToDisplay([]);
                        setSafePortToDisplay([]);
                    }
                    return;
                }
                const newVehicleIds = new Set(data.items.map((e) => e.vehicle.id));
                [...vehicleLocationMap.keys()]
                    .filter((e) => !newVehicleIds.has(e))
                    .forEach((e) => vehicleLocationMap.delete(e));
                const now = new Date();
                const moves = data.items
                    .map((e) => {
                    const lastPosition = e.path?.at(-1);
                    if (!lastPosition) {
                        return undefined;
                    }
                    const prev = vehicleLocationMap.get(e.vehicle.id);
                    const expired = now.getTime() - (prev?.loadTime?.getTime() || 0) > MAP_UPDATE_INTERVAEL_MSEC * 2;
                    const currentCoords = { lat: lastPosition.lat, lng: lastPosition.lng };
                    return {
                        vehicle: e.vehicle,
                        path: e.path,
                        from: !expired && !!prev?.coords ? prev.coords : currentCoords,
                        to: currentCoords,
                        loadTime: now,
                    };
                })
                    .filter((e) => !!e);
                setPreviousLoadTime(now);
                interpolate(moves, previousLoadTime ? now.getTime() - previousLoadTime.getTime() : MAP_UPDATE_INTERVAEL_MSEC);
            },
        },
    ]);
    const interpolate = (moves, loadIntervalMsec) => {
        const loadIntervalSec = loadIntervalMsec / 1000;
        const newPositions = moves?.map((e) => {
            const diff = Math.min(new Date().getTime() - e.loadTime.getTime(), loadIntervalMsec);
            const step = (diff / loadIntervalMsec) * (loadIntervalSec * ANIMATION_FRAME_RATES);
            return {
                vehicle: e.vehicle,
                path: e.path,
                coords: {
                    lat: ((e.to.lat - e.from.lat) / (loadIntervalSec * ANIMATION_FRAME_RATES)) * step * MOVEMENT_SPEED_FACTOR + e.from.lat,
                    lng: ((e.to.lng - e.from.lng) / (loadIntervalSec * ANIMATION_FRAME_RATES)) * step * MOVEMENT_SPEED_FACTOR + e.from.lng,
                },
                heading: (Math.atan2(e.to.lng - e.from.lng, e.to.lat - e.from.lat) * 180.0) / Math.PI,
                loadTime: e.loadTime,
            };
        });
        newPositions?.forEach((e) => vehicleLocationMap.set(e.vehicle.id, e));
        setVehicleLocationMap?.(new Map(vehicleLocationMap));
        if (newPositions?.length > 0) {
            const handler = setTimeout(interpolate, 1000 / ANIMATION_FRAME_RATES, moves, loadIntervalMsec);
            setTimeoutHandler(handler);
            clearTimeout(timeoutHandler);
        }
        setVehicles?.(newPositions);
    };
    const vehiclesRef = React.useRef(vehicles);
    React.useEffect(() => {
        vehiclesRef.current = vehicles;
    }, [vehicles]);
    React.useEffect(() => {
        const oldKeys = new Set(footprints?.items?.map((e) => e.vehicle.id));
        const newKeys = new Set(vehiclesRef.current?.map((e) => e.vehicle.id));
        if (oldKeys.size !== newKeys.size || ![...oldKeys].every((e) => newKeys.has(e))) {
            queryClient.invalidateQueries(API.Vehicles.CacheKey);
        }
    }, [footprints, queryClient]);
    React.useEffect(() => {
        return () => {
            clearTimeout(timeoutHandler);
        };
    }, [timeoutHandler]);
    React.useEffect(() => {
        if (!selectedFlightPathId) {
            return;
        }
        const coords = flightPaths?.items
            ?.filter((e) => e.id === selectedFlightPathId)
            ?.at(0)
            ?.waypoints?.map((e) => e.location);
        if (coords) {
            setBounds?.(coords);
        }
    }, [selectedFlightPathId, flightPaths?.items, setBounds]);
    React.useEffect(() => {
        const map = new Map();
        flightPaths?.items?.forEach((e) => map.set(e.id, e));
        vehicleList?.items?.forEach((e) => {
            const flightPath = e.activeFlightPlan?.flightPath;
            if (flightPath) {
                map.set(flightPath.id, flightPath);
            }
        });
        setFlightPathToDisplay([...map.values()]);
    }, [flightPaths?.items, vehicleList]);
    React.useEffect(() => {
        const map = new Map();
        geoFences?.items?.forEach((e) => map.set(e.id, e));
        vehicleList?.items?.forEach((e) => e.activeFlightPlan?.flightPath?.mission?.geoFences?.forEach((e) => map.set(e.id, e)));
        setGeoFenceToDisplay([...map.values()]);
    }, [geoFences?.items, vehicleList]);
    React.useEffect(() => {
        const map = new Map();
        safePorts?.items?.forEach((e) => map.set(e.id, e));
        vehicleList?.items?.forEach((e) => e.activeFlightPlan?.flightPath?.mission?.safePorts?.forEach((e) => map.set(e.id, e)));
        setSafePortToDisplay([...map.values()]);
    }, [safePorts?.items, vehicleList]);
    React.useEffect(() => {
        if (!loaded) {
            return;
        }
        const markers = vehicles?.map((e) => {
            const loc = e;
            const props = {
                key: e.vehicle.id,
                vehicleId: e.vehicle.id,
                position: loc.coords,
                heading: loc.heading,
                selected: selectedVehicleId === e.vehicle.id,
                satellite: mapTypeId === 'hybrid',
                onVehicleClick: setSelectedVehicleId,
            };
            return e.vehicle.vehicleType === Types.VehicleTypeHoverbike ? (React.createElement(HoverbikeMarker, { ...props })) : e.vehicle.vehicleType === Types.VehicleTypeAsv ? (React.createElement(AsvMarker, { ...props })) : (React.createElement(DroneMarker, { ...props }));
        });
        setVehiclesToDisplay(markers);
    }, [loaded, vehicles, mapTypeId, selectedVehicleId, setSelectedVehicleId]);
    const radarVehiclePaths = vehicles?.length ? vehicles : footprints?.items;
    const radarLocation = React.useMemo(() => pickRadarCenter(center, radarVehiclePaths, selectedVehicleId), [center, radarVehiclePaths, selectedVehicleId]);
    return (React.createElement(PolarisMap, { apiKey: apiKey, draggable: true, locked: true, interactiveLegend: true, on3dSelected: on3dSelected, geoFences: showGeoFence ? geoFenceToDisplay : [], noFlyZones: showNoFlyZone ? noFlyZones?.items : [], safePorts: showSafePort ? safePortToDisplay : [], flightPaths: showFlightPath ? flightPathToDisplay : [], paths: showFootprint ? radarVehiclePaths?.map((e) => e.path) : [], radar: showRadar && radarRadius > 0 && radarLocation
            ? { location: radarLocation, radius: radarRadius }
            : undefined },
        vehiclesToDisplay,
        children));
}
export default React.memo(MonitorMap);
